import { DocumentType, Media, UploadType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import { removeUploadedFile, updateUploadedFileTitle, uploadFile } from '@synergy/data-access';

import { PaloAttachments } from '../../../common/components/palo/palo-attachments';
import { noticeUploaded } from '../../../common/utils/upload-notice';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/**
 * The files of a sales order, for the dock's "Anhänge" tab.
 *
 * What the member sent in comes with their order and is not ours to change; the
 * internal set is.
 */
export const PaloSalesOrderAttachments = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const media = useAppSelector(state => state.media.flat);
    const downloadBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const documentId = useAppSelector(state => state.salesOrders.entity?.id);

    const internal = media.filter(file => file.type === UploadType.Internal);
    const customer = media.filter(file => file.type === UploadType.Customer);

    const drop = (files: File[], titles: string[] = []) => {
        const deduplicated = files
            .map((file, index) => ({ file, title: titles[index] }))
            .filter(
                ({ file }) =>
                    !internal.some(existing => existing.originalName === file.name && existing.size === file.size)
            );

        if (documentId && deduplicated.length > 0) {
            // Said as on every other document: stored the moment it is dropped (R-197).
            void noticeUploaded(
                dispatch,
                deduplicated.map(({ file, title }) =>
                    dispatch(uploadFile(DocumentType.SalesOrder, documentId, file, undefined, title))
                )
            );
        }
    };

    const remove = (file: Media) => {
        if (documentId) {
            dispatch(removeUploadedFile(DocumentType.SalesOrder, documentId, file));
        }
    };

    const rename = (media: Media, title: string) => dispatch(updateUploadedFileTitle(media, title));

    return (
        <PaloAttachments
            groups={[
                {
                    key: 'customer',
                    label: <FormattedMessage id="app.attachments.group.customer" />,
                    files: customer,
                    downloadBaseUrl,
                    readOnly: true,
                },
                {
                    key: 'internal',
                    label: <FormattedMessage id="app.attachments.group.internal" />,
                    files: internal,
                    downloadBaseUrl,
                    onDrop: documentId ? drop : undefined,
                    onRemove: remove,
                    onRename: rename,
                    onCancel: file => file.cancel?.(),
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
            ]}
        />
    );
};
